import {memoize} from 'lodash';
import {DefaultTheme} from 'styled-components';

import {assertUnreachable} from '../../../../core/src/helpers/typescript/typeHelpers';

/** Visual variants for buttons. */
export enum ButtonVariantsEnum {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',

  TOOLTIP_LINK = 'tooltip_link',
  TOOLTIP_SECONDARY = 'tooltip_secondary',
}

export enum ButtonColor {
  DEFAULT = 'default',

  BRAND = 'brand',
  DANGER = 'danger',
  SUCCESS = 'success',
  WARNING = 'warning',
}

type OptionalColorResolver = (color?: `${ButtonColor}`) => string;
type ButtonColors = {
  [T in ButtonVariantsEnum]: {
    text: string | OptionalColorResolver;
    textDisabled: string | undefined;
    background: OptionalColorResolver;
    backgroundHover: OptionalColorResolver;
    backgroundActive: OptionalColorResolver;
    backgroundDisabled: OptionalColorResolver | string | undefined;
    border?: string;
  };
};

function getButtonBackground(theme: DefaultTheme, color: `${ButtonColor}` = 'default') {
  switch (color) {
    case 'default':
      return {
        background: theme.background.fill.primary,
        backgroundActive: theme.background.fill.primaryPressed,
        backgroundHover: theme.background.fill.primaryHover,
        backgroundDisabled: theme.background.fill.primaryDisabled,
      };
    case 'brand':
      return {
        background: theme.background.fill.brand,
        backgroundActive: theme.background.fill.brandPressed,
        backgroundHover: theme.background.fill.brandHover,
        backgroundDisabled: theme.background.fill.brandDisabled,
      };
    case 'danger':
      return {
        background: theme.background.fill.danger,
        backgroundActive: theme.background.fill.dangerPressed,
        backgroundHover: theme.background.fill.dangerHover,
        backgroundDisabled: theme.background.fill.dangerDisabled,
      };
    case 'success':
      return {
        background: theme.background.fill.success,
        backgroundActive: theme.background.fill.successPressed,
        backgroundHover: theme.background.fill.successHover,
        backgroundDisabled: theme.background.fill.successDisabled,
      };
    case 'warning':
      return {
        background: theme.background.fill.caution,
        backgroundActive: theme.background.fill.cautionPressed,
        backgroundHover: theme.background.fill.cautionHover,
        backgroundDisabled: theme.background.fill.warningDisabled,
      };
    default:
      assertUnreachable(color);
      return {
        background: theme.background.fill.primary,
        backgroundActive: theme.background.fill.primaryPressed,
        backgroundHover: theme.background.fill.primaryHover,
        backgroundDisabled: theme.background.fill.primaryDisabled,
      };
  }
}

function getButtonTextColor(
  theme: DefaultTheme,
  variant: ButtonVariantsEnum,
  color: `${ButtonColor}` = 'default',
) {
  switch (color) {
    case 'default':
      if (variant === ButtonVariantsEnum.TERTIARY) {
        return theme.typography.shades.interactive;
      }
      return theme.typography.shades.primary;
    case 'brand':
      return theme.typography.shades.brand;
    case 'danger':
      return theme.typography.shades.danger;
    case 'success':
      return theme.typography.shades.success;
    case 'warning':
      return theme.typography.shades.warning;
    default:
      assertUnreachable(color);
      return theme.typography.shades.interactive;
  }
}

/** Button color constants by variant. */
export const findButtonColors = memoize(
  (theme: DefaultTheme): ButtonColors => ({
    [ButtonVariantsEnum.PRIMARY]: {
      text: theme.typography.shades['on-bg-fill'],
      textDisabled: theme.typography.shades['on-bg-fill-disabled'],
      background: (color) => getButtonBackground(theme, color).background,
      backgroundHover: (color) => getButtonBackground(theme, color).backgroundHover,
      backgroundActive: (color) => getButtonBackground(theme, color).backgroundActive,
      backgroundDisabled: (color) => getButtonBackground(theme, color).backgroundDisabled,
    },
    [ButtonVariantsEnum.SECONDARY]: {
      text: (color) => getButtonTextColor(theme, ButtonVariantsEnum.SECONDARY, color),
      textDisabled: theme.typography.shades.disabled,
      background: () => theme.background.fill.secondary,
      backgroundHover: () => theme.background.fill.secondaryHover,
      backgroundActive: () => theme.background.fill.secondaryPressed,
      backgroundDisabled: theme.background.fill.secondaryDisabled,
      border: theme.border.secondary,
    },
    [ButtonVariantsEnum.TERTIARY]: {
      text: (color) => getButtonTextColor(theme, ButtonVariantsEnum.TERTIARY, color),
      textDisabled: theme.typography.shades.disabled,
      background: () => theme.background.fill.tertiary,
      backgroundHover: () => theme.background.fill.tertiaryHover,
      backgroundActive: () => theme.background.fill.tertiaryPressed,
      backgroundDisabled: undefined,
    },
    [ButtonVariantsEnum.TOOLTIP_LINK]: {
      text: theme.tooltip.button.link.textColor,
      background: () => theme.tooltip.button.link.backgroundColor,
      backgroundHover: () => theme.tooltip.button.link.backgroundHoverColor,
      backgroundActive: () => theme.tooltip.button.link.backgroundColor,

      textDisabled: theme.tooltip.button.link.disabled.textColor,
      backgroundDisabled: theme.tooltip.button.link.disabled.backgroundColor,
    },
    [ButtonVariantsEnum.TOOLTIP_SECONDARY]: {
      text: theme.tooltip.button.secondary.textColor,
      background: () => theme.tooltip.button.secondary.backgroundColor,
      backgroundHover: () => theme.tooltip.button.secondary.backgroundHoverColor,
      backgroundActive: () => theme.tooltip.button.secondary.backgroundColor,

      textDisabled: theme.tooltip.button.secondary.disabled.textColor,
      backgroundDisabled: theme.tooltip.button.secondary.disabled.backgroundColor,
      border: theme.tooltip.button.secondary.borderColor,
    },
  }),
);

const variantsWithBorder: ReadonlyArray<`${ButtonVariantsEnum}`> = [
  ButtonVariantsEnum.SECONDARY,
  ButtonVariantsEnum.TOOLTIP_SECONDARY,
];

export function isVariantWithBorder(variant: `${ButtonVariantsEnum}`) {
  return variantsWithBorder.includes(variant);
}
